import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/api'
import { copyAppearance, defaultAppearance, parseAppearance } from '@/features/appearance/preferences'
import type { AppearancePreferences } from '@/types/preferences'

export const useAppearanceStore = defineStore('appearance', () => {
  const saved = ref(defaultAppearance())
  const userId = ref<string | null>(null)
  const loading = ref(false)
  const loaded = ref(false)
  const saving = ref(false)
  const error = ref<string | null>(null)
  let generation = 0

  async function load(accountId: string | null) {
    const request = ++generation
    userId.value = accountId
    saved.value = defaultAppearance()
    loaded.value = false
    saving.value = false
    error.value = null
    loading.value = !!accountId
    if (!accountId) return
    try {
      const result = await api.getAppearancePreferences(accountId)
      if (request !== generation) return
      saved.value = result === null ? defaultAppearance() : parseAppearance(result)
      loaded.value = true
    } catch (cause) {
      if (request === generation) error.value = cause instanceof Error ? cause.message : 'Could not load appearance.'
    } finally {
      if (request === generation) loading.value = false
    }
  }

  async function save(draft: AppearancePreferences) {
    if (!userId.value || !loaded.value || saving.value) throw new Error('Appearance is not ready to save.')
    const accountId = userId.value
    const request = generation
    const document = parseAppearance(copyAppearance(draft))
    saving.value = true
    try {
      const result = await api.saveAppearancePreferences(accountId, document)
      if (request !== generation) throw new Error('The active account changed. Reopen appearance settings.')
      saved.value = parseAppearance(result)
    } finally {
      if (request === generation) saving.value = false
    }
  }
  return { saved, userId, loading, loaded, saving, error, load, save }
})
